import { Injectable } from '@angular/core';
import { IProductDataV3, Product } from '@df/catalog/product/product';
import { NProductConfiguration } from '@df/module-product-configuration/df-module-product-configuration.interfaces';
import defaults from 'lodash-es/defaults';
import difference from 'lodash-es/difference';
import each from 'lodash-es/each';
import intersection from 'lodash-es/intersection';
import { ProductConfigurationConfigurable } from '../product-configuration-configurable';

@Injectable()
export class ProductConfigurationConfigurableProductDataService {
    /**
     * Configuration
     */
    private config!: NProductConfiguration.Configurable.Config;
    private productConfiguration!: ProductConfigurationConfigurable;

    /**
     * Cachec for matching children
     */
    private childrenMatchCache = new Map<string, NProductConfiguration.Configurable.ChildrenMatchResult>();

    init(config: NProductConfiguration.Configurable.Config, productConfiguration: ProductConfigurationConfigurable) {
        this.config = config;
        this.productConfiguration = productConfiguration;
    }

    /**
     * Sanitizes provided selection
     * Motates the object provided
     *
     * @input Product - configurable product
     */
    sanitize(selection: NProductConfiguration.Configurable.Selection, product: Product<IProductDataV3>) {
        if (!product.isConfigurable) {
            throw new Error(`Product is not configurable (type id: ${product.data?.type_id || 'undefined'})`);
        }
        const configurableOptions = (product.data as IProductDataV3)!.options?.configurable;

        if (!configurableOptions) {
            throw new Error('Configurable options are not provided');
        }
        const emptySelection = this.getEmptySelection(product);

        defaults(selection, emptySelection);

        if (this.config.sanitizeSelection) {
            this.config.sanitizeSelection(selection, product, this.productConfiguration);
        }

        each(selection, (value, code) => {
            // skip invalid keys
            if (!Object.hasOwnProperty.call(emptySelection, code)) {
                delete selection[code];
                return;
            }
            // reset invalid entries
            if (typeof value !== 'number') {
                selection[code] = undefined;
                return;
            }
            // reset if value is not allowed
            if (!configurableOptions[code]?.[value]?.length) {
                selection[code] = undefined;
            }
        });
    }

    /**
     * Match product children by attributes top to bottom
     */
    matchChildren(
        selection: NProductConfiguration.Configurable.Selection,
        product: Product<IProductDataV3>,
        priorityAttributes: string[]
    ): NProductConfiguration.Configurable.ChildrenMatchResult {
        if (!product.isConfigurable) {
            throw new Error(`Product is not configurable (type id: ${product.data?.type_id || 'undefined'})`);
        }

        const cacheId = `${product.id}/` + priorityAttributes.join(',') + '/' + JSON.stringify(selection);

        if (!this.childrenMatchCache.has(cacheId)) {
            let children = product.existingChildren;

            const selectionAttributes = Object.keys(selection);
            const undefinedAttributes = selectionAttributes.filter(i => !selection[i]);
            const filledAttributes = difference(selectionAttributes, undefinedAttributes);
            const attrsList = [...intersection(priorityAttributes, filledAttributes), ...difference(filledAttributes, priorityAttributes)];

            const remainingAttributesIndex = attrsList.findIndex(attrCode => {
                const attrValue = selection[attrCode];
                const matchChildren = children.filter(child => child.data![attrCode] === attrValue);
                if (matchChildren.length) {
                    children = matchChildren;
                } else {
                    return true;
                }
                return false;
            });

            this.childrenMatchCache.set(cacheId, {
                children: children as Product<IProductDataV3>[],
                omittedAttributes: remainingAttributesIndex > 0 ? attrsList.slice(remainingAttributesIndex) : [],
                undefinedAttributes
            });
        }

        return this.childrenMatchCache.get(cacheId)!;
    }

    /**
     * Provides empty selection object with keys matching configurable options
     * @param product Configurable product
     */
    private getEmptySelection(product: Product<IProductDataV3>): NProductConfiguration.Configurable.Selection {
        if (!product.data) {
            throw new Error('Product parent is not provided');
        }
        if (!product.isConfigurable) {
            throw new Error(`Product is not configurable (type id: ${product.data?.type_id || 'undefined'})`);
        }
        const selection: NProductConfiguration.Configurable.Selection = {};

        product.configurableAttributeCodes!.forEach(code => {
            selection[code] = undefined;
        });

        return selection;
    }

    fillBySimple(selection: NProductConfiguration.Configurable.Selection, product: Product<IProductDataV3>, initSelection = false) {
        if (!product.data) {
            throw new Error('Product data is not provided');
        }
        if (!product.isSimple) {
            throw new Error(`Product is not simple (type id: ${product.data?.type_id || 'undefined'})`);
        }

        const parentProduct = product.directParent;
        if (initSelection) {
            parentProduct!.configurableAttributeCodes!.forEach(code => {
                if (!(code in selection)) {
                    selection[code] = undefined;
                }
            });
        }
        each(selection, (value, code) => {
            const productValue = product.data![code];
            if (typeof productValue === 'number') {
                selection[code] = productValue;
            } else {
                const directParentValue = parentProduct?.data?.[code];
                if (typeof directParentValue === 'number') {
                    selection[code] = directParentValue;
                } else {
                    throw new Error(`Configurable options ${code} is not found in product data or parent data`);
                }
            }
        });
    }
}
